import { useCallback, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { Button } from 'primereact/button'
import { ProgressSpinner } from 'primereact/progressspinner'
import { TabPanel, TabView, TabViewTabChangeEvent } from 'primereact/tabview'
import ExportMenu, { ExportFormat } from '../../../components/novarium/ExportMenu'
import NvTooltips from '../../../components/novarium/NvTooltips'
import { clearPersistedTableState } from '../../../components/summary/summaryTable'
import {
  OperatingRoomCancellationsDTO,
  OperatingRoomEvolutionDTO,
  OperatingRoomGroupBy,
  OperatingRoomGroupRowDTO,
  OperatingRoomGroupsDTO,
  OperatingRoomHeatMeasure,
  OperatingRoomHeatmapDTO,
  OperatingRoomOptionsDTO,
  OperatingRoomOutcome,
  OperatingRoomRoomDTO,
  OperatingRoomRoomsDTO,
  OperatingRoomTimesDTO,
} from '../../../dto/operatingRoomIndicators/OperatingRoomIndicatorsDTO'
import useNotify from '../../../hooks/useNotify'
import {
  OperatingRoomTableParams,
  exportOperatingRoomList,
  exportOperatingRoomTab,
  getOperatingRoomCancellations,
  getOperatingRoomEvolution,
  getOperatingRoomGroups,
  getOperatingRoomHeatmap,
  getOperatingRoomOptions,
  getOperatingRoomRooms,
  getOperatingRoomTimes,
  isAccessDenied,
  isRequestCanceled,
} from '../../../services/OperatingRoomIndicatorsController'
import useSectionData from '../../UrgencySummary/useSectionData'
import { OperatingRoomMeasure } from '../operatingRoomEvolutionModel'
import { filtersKey, NO_SELECT_FILTERS, OperatingRoomDrill, isFilterActive, OPERATING_ROOM_MORE_FILTERS } from '../operatingRoomFilterModel'
import { OPERATING_ROOM_TAB_LABEL_ID, OPERATING_ROOM_TABS, OperatingRoomTab } from '../operatingRoomTabs'
import useOperatingRoomFilters from '../useOperatingRoomFilters'
import { OperatingRoomListView } from '../useOperatingRoomGrid'
import useOperatingRoomSummary from '../useOperatingRoomSummary'
import OperatingRoomCancellations from './OperatingRoomCancellations'
import OperatingRoomEvolution from './OperatingRoomEvolution'
import OperatingRoomFilters from './OperatingRoomFilters'
import OperatingRoomGroupedTable from './OperatingRoomGroupedTable'
import OperatingRoomHeatmap from './OperatingRoomHeatmap'
import OperatingRoomIndicators from './OperatingRoomIndicators'
import OperatingRoomList, { LIST_TABLE_ID } from './OperatingRoomList'
import OperatingRoomRooms from './OperatingRoomRooms'
import OperatingRoomTimes from './OperatingRoomTimes'

/** La lista guarda filtros y orden en el navegador (pueden tener nombres de pacientes): la pantalla abre y cierra sin ellos. */
const clearTables = () => clearPersistedTableState(LIST_TABLE_ID)

const DEFAULT_LIST_VIEW: OperatingRoomListView = { outcome: 'COMPLETED', cell: null }

/**
 * Indicadores de quirófano: los filtros al instante (período y «Más filtros»), los indicadores del período y, debajo, las
 * pestañas Evolución | Mapa de calor | Quirófanos | Agrupado | Tiempos | Anulaciones | Lista. El período y los filtros viajan en `filters` y son los
 * mismos en todas las pestañas y en la exportación; la pestaña, las medidas, el agrupamiento y lo que muestra la lista no se
 * pierden al cambiar los filtros. Un número de los indicadores, una fila de Quirófanos o de Agrupado, un número de Tiempos o de
 * Anulaciones y una celda del mapa abren la lista con las cirugías que cuentan (`drill`).
 */
export default function OperatingRoomIndicatorsPage() {
  const intl = useIntl()
  const notify = useNotify()

  const filtersData = useOperatingRoomFilters()
  const { filters, applied, pending, patchFilters } = filtersData
  const { summary, status, deniedKind, retry } = useOperatingRoomSummary(applied)
  const appliedRef = useRef(applied)
  appliedRef.current = applied

  // Antes del primer render de la tabla: abre sin filtros ni orden de una visita anterior
  useState(clearTables)
  useEffect(() => clearTables, [])

  const [tab, setTab] = useState<OperatingRoomTab>('evolution')
  const [measure, setMeasure] = useState<OperatingRoomMeasure>('surgeries')
  const [heatMeasure, setHeatMeasure] = useState<OperatingRoomHeatMeasure>('HOURS')
  const [groupBy, setGroupBy] = useState<OperatingRoomGroupBy>('TYPE')
  const [listView, setListView] = useState<OperatingRoomListView>(DEFAULT_LIST_VIEW)
  // Opciones de los combos y permiso de agrupar por cirujano: se piden una sola vez al abrir la pantalla
  const [options, setOptions] = useState<OperatingRoomOptionsDTO | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    getOperatingRoomOptions(controller.signal)
      .then(setOptions)
      .catch((error: unknown) => {
        // La pantalla sigue funcionando: los combos quedan sin opciones y sin «Cirujano» en el agrupado
        if (!controller.signal.aborted && !isRequestCanceled(error) && !isAccessDenied(error)) {
          notify.error(intl.formatMessage({ id: 'OperatingRoomIndicatorsOptionsError' }))
        }
      })
    return () => controller.abort()
    // Se pide una sola vez por apertura de la pantalla
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const canGroupBySurgeon = options?.canGroupBySurgeon === true
  // Sin la acción 46 «Cirujano» no se ofrece: si quedó elegido (no debería), se vuelve a «Tipo de cirugía»
  const shownGroupBy: OperatingRoomGroupBy = groupBy === 'SURGEON' && !canGroupBySurgeon ? 'TYPE' : groupBy

  // Lo último que pidió la lista (filtros de pantalla, de columna, buscador y orden): es lo que ve el usuario
  const lastLoad = useRef<OperatingRoomTableParams | undefined>(undefined)
  const onListLoad = useCallback((params: OperatingRoomTableParams) => { lastLoad.current = params }, [])

  // Cada pestaña se pide recién al abrirla y de nuevo con cada cambio de filtros (o de su medida o agrupación)
  const summaryReady = summary !== null && status !== 'error'
  const baseKey = filtersKey(applied)
  const evolution = useSectionData<OperatingRoomEvolutionDTO>(
    summaryReady && tab === 'evolution' ? `${baseKey}|evolution` : null,
    (signal) => getOperatingRoomEvolution(appliedRef.current, 'AUTO', signal),
  )
  const heatmap = useSectionData<OperatingRoomHeatmapDTO>(
    summaryReady && tab === 'heatmap' ? `${baseKey}|heatmap|${heatMeasure}` : null,
    (signal) => getOperatingRoomHeatmap(appliedRef.current, heatMeasure, signal),
  )
  const rooms = useSectionData<OperatingRoomRoomsDTO>(
    summaryReady && tab === 'rooms' ? `${baseKey}|rooms` : null,
    (signal) => getOperatingRoomRooms(appliedRef.current, signal),
  )
  const groups = useSectionData<OperatingRoomGroupsDTO>(
    summaryReady && tab === 'grouped' ? `${baseKey}|groups|${shownGroupBy}` : null,
    (signal) => getOperatingRoomGroups(appliedRef.current, shownGroupBy, signal),
  )

  const times = useSectionData<OperatingRoomTimesDTO>(
    summaryReady && tab === 'times' ? `${baseKey}|times` : null,
    (signal) => getOperatingRoomTimes(appliedRef.current, signal),
  )
  const cancellations = useSectionData<OperatingRoomCancellationsDTO>(
    summaryReady && tab === 'cancellations' ? `${baseKey}|cancellations` : null,
    (signal) => getOperatingRoomCancellations(appliedRef.current, signal),
  )

  /**
   * «Exportar» baja lo que se ve: en la lista, lo último que pidió su tabla (los filtros de pantalla de ahora más los de
   * columna, el buscador, el orden, el estado y la celda); en el resto, la pestaña con sus filtros y su medida o agrupamiento.
   */
  const exportCurrent = useCallback((format: ExportFormat) => {
    const current = appliedRef.current
    switch (tab) {
      case 'list':
        return exportOperatingRoomList(format, { ...lastLoad.current, filters: current })
      case 'heatmap':
        return exportOperatingRoomTab('HEATMAP', current, { measure: heatMeasure }, format)
      case 'rooms':
        return exportOperatingRoomTab('ROOMS', current, {}, format)
      case 'grouped':
        return exportOperatingRoomTab('GROUPS', current, { groupBy: shownGroupBy }, format)
      case 'times':
        return exportOperatingRoomTab('TIMES', current, {}, format)
      case 'cancellations':
        return exportOperatingRoomTab('CANCELLATIONS', current, {}, format)
      default:
        return exportOperatingRoomTab('EVOLUTION', current, { granularity: 'AUTO' }, format)
    }
  }, [tab, heatMeasure, shownGroupBy])

  const title = intl.formatMessage({ id: 'OperatingRoomIndicatorsTitle' })

  if (status === 'denied') {
    return (
      <div className="nv-scope nv-page">
        <header className="nv-page-head">
          <h1 className="nv-h1">{title}</h1>
        </header>
        <p className="nv-note" role="status">
          <i className="pi pi-info-circle" aria-hidden="true" />
          {intl.formatMessage({ id: deniedKind === 'surgeon' ? 'OperatingRoomIndicatorsSurgeonDenied' : 'OperatingRoomIndicatorsAccessDenied' })}
        </p>
      </div>
    )
  }

  // Los datos que se ven no son los de los filtros: se están consultando o el cambio espera el retardo
  const busy = status === 'loading' || pending
  const hasChips = OPERATING_ROOM_MORE_FILTERS.some((field) => isFilterActive(filters, field))
  const { current } = summary ?? {}
  const isEmpty = summaryReady && current !== undefined
    && current.surgeries === 0 && current.hoursUsed === 0 && current.annulled === 0 && current.rescheduled === 0 && current.missed === 0
  const showContent = summaryReady && !isEmpty

  const exportDisabledReason = busy ? intl.formatMessage({ id: 'OperatingRoomIndicatorsExportLoading' })
    : status === 'error' ? intl.formatMessage({ id: 'OperatingRoomIndicatorsExportBlocked' })
      : isEmpty ? intl.formatMessage({ id: 'OperatingRoomIndicatorsExportEmpty' })
        : undefined

  /** Abre la lista con lo que pide el número, la fila o la celda: filtros de pantalla, estado de las cirugías y celda del mapa. */
  const drill = (request: OperatingRoomDrill) => {
    if (request.patch) patchFilters(request.patch)
    setListView({ outcome: request.outcome ?? 'COMPLETED', cell: request.cell ?? null })
    setTab('list')
  }

  const drillRoom = (room: OperatingRoomRoomDTO) => {
    if (room.roomId !== null) drill({ patch: { roomIds: [room.roomId] } })
  }

  const drillGroup = (by: OperatingRoomGroupBy, row: OperatingRoomGroupRowDTO) => {
    const id = row.id
    if (by === 'AGENDA') {
      // «Sin agenda» son las cirugías fuera de agenda; una fila con id es un bloque de plantilla
      if (id === null) drill({ patch: { agenda: 'OUT_OF_AGENDA', agendaBlock: null } })
      else drill({ patch: { agendaBlock: { id, name: agendaBlockName(row) } } })
      return
    }
    if (id === null) return
    if (by === 'TYPE') drill({ patch: { surgeryTypeId: id } })
    else if (by === 'SURGEON') drill({ patch: { surgeonId: id } })
    else if (by === 'ANESTHESIA') drill({ patch: { anesthesiaId: id } })
    else if (by === 'FINANCIER') drill({ patch: { financierId: id } })
    else if (by === 'CODE') drill({ patch: { nomenclator: { id, name: [row.code, row.name].filter(Boolean).join(' - ') } } })
  }

  /** El nombre de un bloque de agenda en su chip: «plantilla · quirófano». */
  const agendaBlockName = (row: OperatingRoomGroupRowDTO): string => [row.name, row.roomName].filter(Boolean).join(' · ')

  /** «Ver cirugías» y «Ver solo este día» del panel del día. */
  const viewDay = (from: string, to: string, toList: boolean) => {
    filtersData.viewRange(from, to)
    if (toList) {
      setListView(DEFAULT_LIST_VIEW)
      setTab('list')
    }
  }

  return (
    <div className="nv-scope nv-page nv-or-page">
      <NvTooltips />
      <div className="nv-page-head">
        <div className="nv-head-row">
          <h1 className="nv-h1">{title}</h1>
          <ExportMenu
            fallbackFileName="indicadores-de-quirofano"
            disabled={exportDisabledReason !== undefined}
            disabledReason={exportDisabledReason}
            onExport={exportCurrent}
          />
        </div>
      </div>

      <OperatingRoomFilters data={filtersData} options={options} />

      {filtersData.rangeOrigin && (
        <div className="nv-or-range-back">
          <Button
            type="button"
            link
            icon="pi pi-angle-double-left"
            label={intl.formatMessage({ id: 'OperatingRoomIndicatorsReturnTo' }, { period: filtersData.rangeOrigin.label })}
            onClick={filtersData.returnToPreviousRange}
          />
        </div>
      )}

      <div className="nv-or-results" aria-busy={busy}>
        {busy && (
          <div className="nv-or-loading" role="status">
            <div className="nv-loading nv-or-loading__box">
              <ProgressSpinner strokeWidth="4" style={{ width: 24, height: 24 }} />
              <span>{intl.formatMessage({ id: 'OperatingRoomIndicatorsLoading' })}</span>
            </div>
          </div>
        )}

        {status === 'error' && !busy && (
          <div className="nv-card nv-or-error" role="alert">
            <p className="nv-note">
              <i className="pi pi-info-circle" aria-hidden="true" />
              {intl.formatMessage({ id: 'OperatingRoomIndicatorsQueryError' })}
            </p>
            <Button type="button" className="buttonMain2" outlined label={intl.formatMessage({ id: 'OperatingRoomIndicatorsRetry' })} onClick={retry} />
          </div>
        )}

        {isEmpty && (
          <div className={`nv-or-no-results${busy ? ' nv-or-blurred' : ''}`}>
            <p className="nv-muted">{intl.formatMessage({ id: 'OperatingRoomIndicatorsEmpty' })}</p>
            {hasChips && (
              <Button
                type="button"
                link
                label={intl.formatMessage({ id: 'OperatingRoomIndicatorsClearFilters' })}
                onClick={() => patchFilters(NO_SELECT_FILTERS)}
              />
            )}
          </div>
        )}

        {showContent && summary && (
          <div className={busy ? 'nv-or-blurred' : undefined}>
            <OperatingRoomIndicators summary={summary} onDrill={drill} />
          </div>
        )}
      </div>

      {showContent && summary && (
        <TabView
          activeIndex={OPERATING_ROOM_TABS.indexOf(tab)}
          onTabChange={(event: TabViewTabChangeEvent) => setTab(OPERATING_ROOM_TABS[event.index])}
        >
          {OPERATING_ROOM_TABS.map((name) => (
            <TabPanel key={name} header={intl.formatMessage({ id: OPERATING_ROOM_TAB_LABEL_ID[name] })}>
              {name === 'evolution' && (
                <div className={busy ? 'nv-or-blurred' : undefined}>
                  <OperatingRoomEvolution
                    summary={summary}
                    evolution={evolution}
                    filters={applied}
                    measure={measure}
                    onMeasureChange={setMeasure}
                    onViewRange={(from, to) => viewDay(from, to, false)}
                    onViewList={(from, to) => viewDay(from, to, true)}
                  />
                </div>
              )}
              {name === 'heatmap' && (
                <div className={busy ? 'nv-or-blurred' : undefined}>
                  <OperatingRoomHeatmap
                    heatmap={heatmap}
                    measure={heatMeasure}
                    onMeasureChange={setHeatMeasure}
                    onCell={(dayOfWeek, hour, cellMeasure) => drill({ cell: { dayOfWeek, hour, measure: cellMeasure } })}
                  />
                </div>
              )}
              {name === 'rooms' && (
                <div className={busy ? 'nv-or-blurred' : undefined}>
                  <OperatingRoomRooms rooms={rooms} onDrill={drillRoom} />
                </div>
              )}
              {name === 'grouped' && (
                <section className="nv-section" aria-label={intl.formatMessage({ id: 'OperatingRoomIndicatorsTabGrouped' })}>
                  <OperatingRoomGroupedTable
                    groupBy={shownGroupBy}
                    onGroupByChange={setGroupBy}
                    canGroupBySurgeon={canGroupBySurgeon}
                    groups={groups}
                    onDrill={drillGroup}
                  />
                </section>
              )}
              {name === 'times' && (
                <div className={busy ? 'nv-or-blurred' : undefined}>
                  <OperatingRoomTimes times={times} onDrill={drill} />
                </div>
              )}
              {name === 'cancellations' && (
                <div className={busy ? 'nv-or-blurred' : undefined}>
                  <OperatingRoomCancellations cancellations={cancellations} onDrill={drill} />
                </div>
              )}
              {name === 'list' && (
                <OperatingRoomList
                  filters={applied}
                  view={listView}
                  onOutcomeChange={(outcome: OperatingRoomOutcome) => setListView((view) => ({ ...view, outcome }))}
                  onClearCell={() => setListView((view) => ({ ...view, cell: null }))}
                  onLoad={onListLoad}
                  showSurgeon={canGroupBySurgeon}
                />
              )}
            </TabPanel>
          ))}
        </TabView>
      )}
    </div>
  )
}
